/**
 * Wire protocol for session-level actions (dice rolls, log broadcasts).
 *
 * TypeScript mirror of src/lambda/shared/GoobDoobVtt.Domain/SessionMessages.cs.
 * Both implementations must stay in lockstep — when one changes, the other has
 * to change too.
 *
 * Session actions are distinct from runtime actions: they're not tied to a
 * single character's state and don't carry version numbers. Dice rolls are the
 * first action in this namespace; future candidates include initiative rolls,
 * rest broadcasts, and DM announcements.
 */

export const SessionActions = {
  Roll: 'session/roll',
  Start: 'session/start',
  End: 'session/end',
  /** DM switches which scene is live for the active session. */
  SelectMap: 'session/select-map',
} as const;

export const SessionServerTypes = {
  LogEntry: 'session/log-entry',
  SessionStatus: 'session/status',
} as const;

// ----------------- inbound -----------------

/**
 * Client-resolved dice roll. The client computes the roll via
 * crypto.getRandomValues and ships the results to the server for audit and
 * broadcast. The server never re-rolls.
 */
export interface RollRequest {
  action: typeof SessionActions.Roll;
  /** Standard dice notation, e.g. "2d6+3", "1d20". */
  expression: string;
  /** Individual die results in roll order. */
  results: number[];
  /** Computed total including modifier. */
  total: number;
  /** Optional label — "attack roll", "fire bolt damage", etc. */
  reason?: string;
  /** When true the server only echoes to the DM + rolling player, not the full session. */
  whisper?: boolean;
  /**
   * Character the roll was made for. Optional — lets the shared roll feed show
   * a character name instead of an account handle and character-scopes the
   * audit row. Omitted by rolls that aren't tied to a character (DM rolls).
   */
  characterId?: string;
  /** Character display name at roll time (denormalised for the feed). */
  characterName?: string;
  /**
   * Where the dice were physically rolled. "sheet" = the in-app roller;
   * "manual" = a player logging a physical / D&D Beyond roll after the fact
   * from the roll feed. Server treats a missing value as "sheet".
   */
  source?: RollSource;
}

/** See {@link RollRequest.source}. Mirrors RollSources in SessionMessages.cs. */
export type RollSource = 'sheet' | 'manual';

// ----------------- outbound -----------------

/**
 * Broadcast to all session connections for the session log panel. Both dice
 * rolls and runtime mutations produce these; the `kind` field discriminates
 * so the UI can pick a rendering style.
 */
export interface LogEntryPayload {
  id: string;
  ts: string;
  actorUsername: string;
  /** "roll" or "mutation" — drives icon and formatting in the log panel. */
  kind: 'roll' | 'mutation';
  /** Human-readable summary line. */
  summary: string;
  /** Dice expression for roll entries; action string for mutations. */
  expression?: string;
  /** Individual die results for roll entries. */
  results?: number[];
  /** Computed total for roll entries. */
  total?: number;
  /** Optional reason/label. */
  reason?: string;
  /** True when only the roller and the DM received this entry. */
  whisper?: boolean;
  /** Character the roll belongs to, when the client said (see {@link RollRequest.characterId}). */
  characterId?: string | null;
  /** Character display name at roll time, when the client said. */
  characterName?: string | null;
  /** "sheet" (in-app) or "manual" (logged off-app). Older servers omit it — treat as "sheet". */
  source?: RollSource | null;
}

// ---- session lifecycle ----

/** DM sends this to start the active session. */
export interface SessionStartRequest {
  action: typeof SessionActions.Start;
  currentMapId?: string;
}

/** DM sends this to end the active session. */
export interface SessionEndRequest {
  action: typeof SessionActions.End;
}

/**
 * DM sends this to flip the active session onto a different scene.
 * `mapId` is null/empty to clear the active scene. The server echoes an
 * updated {@link SessionStatusPayload} so every client flips together.
 */
export interface SessionSelectMapRequest {
  action: typeof SessionActions.SelectMap;
  mapId: string | null;
}

/** Broadcast when the session starts or ends. */
export interface SessionStatusPayload {
  active: boolean;
  sessionId: string;
  startedAt?: string;
  currentMapId?: string;
  dmUsername?: string;
}

/** A connected player, returned by GET /sessions/:id/status. */
export interface ConnectedPlayer {
  sub: string;
  username: string;
  role: 'dm' | 'player';
}

/** Response from GET /sessions/:id/status. */
export interface SessionStatusResponse {
  active: boolean;
  sessionId: string;
  startedAt?: string | null;
  currentMapId?: string | null;
  dmUsername?: string | null;
  connectedPlayers: ConnectedPlayer[];
}
